'use client';

// React Imports
import { useEffect, useRef } from 'react';

// Next Imports
import Link from 'next/link';

// MUI Imports
import { styled, useColorScheme, useTheme } from '@mui/material/styles';

// Type Imports
import type { Mode, SystemMode } from '@core/types';

// Component Imports
import VerticalNav, { NavHeader } from '@menu/vertical-menu';
import VerticalMenu from './VerticalMenu';
import Logo from '@components/layout/shared/Logo';

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav';
import { useSettings } from '@core/hooks/useSettings';

// Style Imports
import navigationCustomStyles from '@core/styles/vertical/navigationCustomStyles';

type Props = {
    mode: Mode;
    systemMode: SystemMode;
};

const StyledBoxForShadow = styled('div')(({ theme }) => ({
    top: 60,
    left: -8,
    zIndex: 2,
    opacity: 0,
    position: 'absolute',
    pointerEvents: 'none',
    width: 'calc(100% + 15px)',
    height: theme.mixins.toolbar.minHeight,
    transition: 'opacity .15s ease-in-out',
    background: `linear-gradient(var(--mui-palette-background-default) ${
        theme.direction === 'rtl' ? '95%' : '5%'
    }, rgb(var(--mui-palette-background-defaultChannel) / 0.85) 30%, rgb(var(--mui-palette-background-defaultChannel) / 0.5) 65%, rgb(var(--mui-palette-background-defaultChannel) / 0.3) 75%, transparent)`,
    '&.scrolled': {
        opacity: 1,
    },
}));

const VerticalNavigation = (props: Props) => {
    // Props
    const { mode, systemMode } = props;

    // Hooks
    const verticalNavOptions = useVerticalNav();
    const { settings } = useSettings();
    const { mode: muiMode, systemMode: muiSystemMode } = useColorScheme();
    const theme = useTheme();

    // Refs
    const shadowRef = useRef(null);

    // Vars
    const { collapseVerticalNav, isBreakpointReached } = verticalNavOptions;

    const isServer = typeof window === 'undefined';
    const isSemiDark = settings.semiDark;
    let isDark;

    if (isServer) {
        isDark = mode === 'system' ? systemMode === 'dark' : mode === 'dark';
    } else {
        isDark =
            muiMode === 'system'
                ? muiSystemMode === 'dark'
                : muiMode === 'dark';
    }

    const scrollMenu = (container: any, isPerfectScrollbar: boolean) => {
        container =
            isBreakpointReached || !isPerfectScrollbar
                ? container.target
                : container;

        if (shadowRef && container.scrollTop > 0) {
            // @ts-ignore
            if (!shadowRef.current.classList.contains('scrolled')) {
                // @ts-ignore
                shadowRef.current.classList.add('scrolled');
            }
        } else {
            // @ts-ignore
            shadowRef.current.classList.remove('scrolled');
        }
    };

    useEffect(() => {
        if (settings.layout === 'collapsed') {
            collapseVerticalNav(true);
        } else {
            collapseVerticalNav(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.layout]);

    return (
        // eslint-disable-next-line lines-around-comment
        // Sidebar Vertical Menu
        <VerticalNav
            customStyles={navigationCustomStyles(verticalNavOptions, theme)}
            collapsedWidth={68}
            backgroundColor='var(--mui-palette-background-default)'
            // eslint-disable-next-line lines-around-comment
            // The following condition adds the data-mui-color-scheme='dark' attribute to the VerticalNav component
            // when semiDark is enabled and the mode or systemMode is light
            {...(isSemiDark &&
                !isDark && {
                    'data-mui-color-scheme': 'dark',
                })}
        >
            {/* Nav Header including Logo & nav toggle icons  */}
            <NavHeader>
                <Link href='/'>
                    <Logo />
                </Link>
            </NavHeader>
            <StyledBoxForShadow ref={shadowRef} />
            <VerticalMenu scrollMenu={scrollMenu} />
        </VerticalNav>
    );
};

export default VerticalNavigation;
